// /* eslint-disable no-nested-ternary */
// /**
//  * 判断拖拽的素材是在轨道的什么位置
//  */

import { Position, Rect } from '@com/type'
import { TrackGap, TrackItem, TrackSegment } from '../types'
import { getSegmentRect, getTrackRect } from './track-rect'
import { SegmentPositionOptions } from './track-coord'
import { add } from '@com/utils/math'
import { TimelineGap } from '@com/components/timeline/type'
import { resolveSegmentFramesIfGap } from './segment'

export type RelativePosition = 'top' | 'middle' | 'bottom'
export interface InsertPosition {
  track: TrackItem
  position: RelativePosition
}

export interface InsertResult extends InsertPosition {
  start: TrackSegment['start']
  frames: TrackSegment['frames']
}

function getRelativePosition(
  trackRect: Rect,
  segmentRect: Rect,
  // 0 ~ 1
  scopeHeightPercent = 1,
): RelativePosition {
  const margin = trackRect.height * ((1 - scopeHeightPercent) / 2)

  const segmentRectMiddleY = getBaseline(segmentRect)
  if (segmentRectMiddleY < trackRect.y + margin) {
    return 'top'
  } else if (segmentRectMiddleY > trackRect.y + trackRect.height - margin) {
    return 'bottom'
  }
  return 'middle'
}

function getBaseline(rect: Rect): number {
  return rect.y + rect.height / 2
}
/**
 * 在hover中的轨道找到匹配度最高的
 * 根据竖直方式两者中间的相对位置距离
 */
function getInsertPosition(options: {
  segmentRect: Rect
  gap: TrackGap
  tracks: TrackItem[]
  segment: TrackSegment
}): InsertPosition {
  const { tracks, gap, segment, segmentRect } = options
  const trackType = tracks.find((t) => t.id === segment.trackId)?.type ?? ''
  const segmentRectMiddleY = getBaseline(segmentRect)

  let targetTrack = tracks.find((track) => {
    const trackRect = getTrackRect(track, {
      gap,
      tracks,
    })

    return (
      segmentRectMiddleY >= trackRect.y &&
      segmentRectMiddleY <= trackRect.y + trackRect.height
    )
  })

  if (!targetTrack) {
    // 按视图
    const viewTracks = [...tracks]
    targetTrack =
      viewTracks.find((track) => {
        const trackRect = getTrackRect(track, {
          gap,
          tracks,
        })
        return segmentRectMiddleY < trackRect.y
      }) ?? viewTracks[viewTracks.length - 1]!
  }

  const canInsertScopeHeightPercent = 0.8

  // 不同轨道类型的，不允许存放
  let scopeHeightPercent = 0
  if (targetTrack.type === trackType) {
    scopeHeightPercent = canInsertScopeHeightPercent
  }

  return {
    track: targetTrack,
    position: getRelativePosition(
      getTrackRect(targetTrack, {
        gap,
        tracks,
      }),
      segmentRect,
      scopeHeightPercent,
    ),
  }
}

export type GetTrackInsertPositionPayload<
  S extends TrackSegment = TrackSegment,
  T extends TrackItem = TrackItem,
> = {
  targets: S[] // 保证同一轨道的元件集合
  position: Position // 集合中任意一个的坐标
  activeSegments: S[]
  gaps: TimelineGap[]
} & SegmentPositionOptions<T>
export function getTrackInsertPosition(
  options: GetTrackInsertPositionPayload,
): InsertResult {
  const { targets, position, tracks, gap, gaps, frameWidth, activeSegments } =
    options

  const segment = targets[0]
  if (!segment) {
    throw new Error('getTrackInsertPosition: segment is not found')
  }

  const segmentRect: Rect = {
    ...getSegmentRect(segment, {
      gap,
      tracks,
      frameWidth,
      gaps,
    }),
    x: position.x,
    y: position.y,
  }

  const insertPosition = getInsertPosition({
    tracks,
    segmentRect,
    segment,
    gap,
  })

  // 检查是否位置冲突了
  if (insertPosition.position === 'middle') {
    const segments = [...(insertPosition.track.segments ?? [])].filter(
      (item) =>
        ![...activeSegments.map((s) => s.id), segment.id].includes(item.id),
    )

    if (
      targets.some((_target) =>
        segments.some((item) => isTimeConflict(_target, item)),
      )
    ) {
      const p = getRelativePosition(
        getTrackRect(insertPosition.track, {
          gap,
          tracks,
        }),
        segmentRect,
        0,
      )

      insertPosition.position = p === 'middle' ? 'top' : p
    }
  }

  return {
    position: insertPosition.position,
    track: insertPosition.track,
    start: segment.start,
    frames: segment.frames,
  }
}

export function getConflictSegment(
  segment: TrackSegment,
  track: TrackItem,
): TrackSegment | undefined {
  const segments = [...(track.segments ?? [])].sort((a, b) => a.start - b.start)

  return segments.find((s) => {
    if (s.id === segment.id) {
      return false
    }

    // 由于新的segment没有轨道信息，需要保证在同一水平上进行冲突判断
    return isTimeConflict(segment, s)
  })
}

export function isTimeConflict(
  segment: TrackSegment,
  target: TrackSegment,
): boolean {
  const segmentStartTime = segment.start
  const segmentEndTime = add(segment.start, resolveSegmentFramesIfGap(segment))

  const targetStartTime = target.start
  const targetEndTime = add(target.start, resolveSegmentFramesIfGap(target))

  // 验证target在左边
  if (segmentStartTime >= targetEndTime) {
    return false
  }

  // 验证target在右边
  if (segmentEndTime <= targetStartTime) {
    return false
  }

  return true
}
